import { useEffect, useRef, useState } from "react";
import { Eye, Loader2, RotateCcw, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMessage } from "@/lib/api";
import { Loading, ErrorBox } from "@/components/common";

const FIELDS = [
  { name: "subject", label: "Objet" }, { name: "title", label: "Titre" },
  { name: "body", label: "Message principal", area: true }, { name: "secondary_text", label: "Texte secondaire", area: true },
  { name: "action_label", label: "Libellé du bouton", action: true },
];
const TONE = { standard: "Standard", information: "Information", warning: "Avertissement", alert: "Alerte" };
const pick = (t) => Object.fromEntries(FIELDS.map((f) => [f.name, t[f.name] || ""]));

const Preview = ({ data, onClose }) => (
  <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-3xl" data-testid="email-preview-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
      <DialogHeader><DialogTitle>Aperçu</DialogTitle><DialogDescription>Données fictives · rendu réel Safe Start</DialogDescription></DialogHeader>
      <p className="text-sm"><span className="text-muted-foreground">Objet : </span><strong data-testid="email-preview-subject">{data?.subject}</strong></p>
      <iframe title="Aperçu de l'e-mail" sandbox="" tabIndex={-1} srcDoc={data?.html} data-testid="email-preview-frame" className="h-[60vh] w-full rounded-md border border-border bg-white" />
    </DialogContent>
  </Dialog>
);

const Editor = ({ tplKey, onSaved }) => {
  const [t, setT] = useState(null);
  const [f, setF] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const refs = useRef({});
  const [focus, setFocus] = useState("body");
  const load = () => api.get(`/platform/email-templates/${tplKey}`).then((r) => { setT(r.data); setF(pick(r.data)); setErrors({}); });
  useEffect(() => { load(); }, [tplKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const insert = (v) => {
    const el = refs.current[focus];
    const token = `{{${v}}}`;
    const cur = f[focus] || "";
    const s = el ? el.selectionStart ?? cur.length : cur.length;
    const e = el ? el.selectionEnd ?? cur.length : cur.length;
    setF({ ...f, [focus]: cur.slice(0, s) + token + cur.slice(e) });
    requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(s + token.length, s + token.length); } });
  };
  const fail = (e) => {
    const d = e.response?.data?.detail;
    if (d?.errors) setErrors(d.errors);
    toast.error(d?.message || errMessage(e));
  };
  const run = async (kind) => {
    setBusy(kind); setErrors({});
    try {
      if (kind === "save") { const r = await api.put(`/platform/email-templates/${tplKey}`, f); setT(r.data); toast.success("Modèle enregistré."); onSaved(); }
      if (kind === "preview") setPreview((await api.post(`/platform/email-templates/${tplKey}/preview`, { draft: f })).data);
      if (kind === "test") toast.success(`E-mail de test envoyé à ${(await api.post(`/platform/email-templates/${tplKey}/test`, { draft: f })).data.to}.`);
      if (kind === "restore") { const r = await api.post(`/platform/email-templates/${tplKey}/restore`); setT(r.data); setF(pick(r.data)); setConfirm(false); toast.success("Modèle Safe Start restauré."); onSaved(); }
    } catch (e) { fail(e); } finally { setBusy(""); }
  };
  if (!t) return <Loading />;
  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-5" data-testid={`email-template-editor-${tplKey}`}>
      <div>
        <p className="text-lg font-semibold">{t.name}</p>
        <p className="text-sm text-muted-foreground">{t.description}</p>
        <p className="mt-1 text-xs text-muted-foreground" data-testid="email-template-status">
          Niveau : {TONE[t.tone]} · {t.customized ? `Personnalisé${t.updated_by_name ? ` par ${t.updated_by_name}` : ""}${t.updated_at ? ` le ${new Date(t.updated_at).toLocaleString("fr-FR")}` : ""}` : "Modèle Safe Start par défaut"}
        </p>
      </div>
      <div className="rounded-md border border-dashed border-amber-500/60 p-3" data-testid="email-template-variables">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Variables disponibles</p>
        <p className="text-xs text-muted-foreground">Cliquez pour insérer dans le champ en cours d'édition ({FIELDS.find((x) => x.name === focus)?.label}).</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {t.variables.map((v) => (
            <button key={v.key} type="button" data-testid={`email-var-${v.key}`} onMouseDown={(e) => e.preventDefault()} onClick={() => insert(v.key)}
              className="rounded-full border border-amber-500/60 bg-amber-500/10 px-3 py-1 text-sm transition-colors hover:bg-amber-500/25">{v.label}</button>
          ))}
        </div>
      </div>
      {FIELDS.filter((x) => !x.action || t.has_action).map((x) => {
        const C = x.area ? Textarea : Input;
        return (
          <div key={x.name}>
            <label htmlFor={`tpl-${x.name}`} className="text-sm font-semibold">{x.label}</label>
            <C id={`tpl-${x.name}`} data-testid={`email-field-${x.name}`} ref={(el) => { refs.current[x.name] = el; }} value={f[x.name]} onFocus={() => setFocus(x.name)}
              onChange={(e) => setF({ ...f, [x.name]: e.target.value })} className={`mt-1.5 ${x.area ? "min-h-[120px]" : "h-11"} text-base`} />
            {errors[x.name] && <p role="alert" data-testid={`email-field-${x.name}-error`} className="mt-1 text-sm text-red-400">{errors[x.name]}</p>}
          </div>
        );
      })}
      {t.has_action && <p className="text-xs text-muted-foreground">Le lien du bouton est généré automatiquement par Safe Start.</p>}
      <div className="flex flex-wrap gap-2">
        <Button data-testid="email-template-save" onClick={() => run("save")} disabled={!!busy}>{busy === "save" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Enregistrer</Button>
        <Button variant="outline" data-testid="email-template-preview" onClick={() => run("preview")} disabled={!!busy}><Eye className="mr-2 h-4 w-4" /> Aperçu</Button>
        <Button variant="outline" data-testid="email-template-test" onClick={() => run("test")} disabled={!!busy}>{busy === "test" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} Envoyer un test</Button>
        <Button variant="ghost" data-testid="email-template-restore" onClick={() => setConfirm(true)} disabled={!!busy || !t.customized}><RotateCcw className="mr-2 h-4 w-4" /> Restaurer le modèle Safe Start</Button>
      </div>
      <Preview data={preview} onClose={() => setPreview(null)} />
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="email-restore-dialog">
          <DialogHeader><DialogTitle>Restaurer le modèle Safe Start ?</DialogTitle><DialogDescription>Votre personnalisation de « {t.name} » sera supprimée et le texte par défaut redeviendra actif immédiatement.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirm(false)}>Annuler</Button><Button data-testid="email-restore-confirm" onClick={() => run("restore")} disabled={!!busy}>Restaurer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const EmailTemplates = () => {
  const [list, setList] = useState(null);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(null);
  const load = () => api.get("/platform/email-templates").then((r) => { setList(r.data); setSel((s) => s || r.data[0]?.key); }).catch((e) => setError(errMessage(e)));
  useEffect(() => { load(); }, []);
  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!list) return <Loading />;
  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]" data-testid="email-templates">
      <div className="space-y-2">
        {list.map((t) => (
          <button key={t.key} type="button" data-testid={`email-template-${t.key}`} onClick={() => setSel(t.key)}
            className={`w-full rounded-lg border p-3 text-left transition-colors ${sel === t.key ? "border-amber-500 bg-amber-500/10" : "border-border hover:border-slate-500"}`}>
            <span className="block text-sm font-semibold">{t.name}</span>
            <span className="text-xs text-muted-foreground">{t.customized ? "Personnalisé" : "Modèle Safe Start"}</span>
          </button>
        ))}
      </div>
      {sel && <Editor key={sel} tplKey={sel} onSaved={load} />}
    </div>
  );
};
